<template>
  <v-card
    v-if="recording"
    class="macro-recorder-bar d-print-none"
    elevation="8"
    width="400"
    data-cy="macro-bar"
  >
    <v-card-title class="py-2 subtitle-1">
      <v-icon color="red" class="mr-2 macro-recording-dot">mdi-record</v-icon>
      Recording print macro
      <v-spacer/>
      <v-btn icon small @click="showSteps = !showSteps">
        <v-icon>{{ showSteps ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-subtitle class="pb-1">
      <strong>{{ recording.name || 'untitled' }}</strong>
      <template v-if="recording.baseline"> &middot; {{ describeTarget(recording.baseline) }}</template>
      <template v-else> &middot; production days: taken from the first page you add</template>
      <br/>
      {{ recording.steps.length }} page{{ recording.steps.length === 1 ? '' : 's' }} added.
      Open a page, pick its production or shipping group and dates, then add it. The pages are
      printed when the macro runs, not now.
    </v-card-subtitle>
    <v-list dense v-if="showSteps && recording.steps.length > 0" class="py-0" data-cy="macro-page-list">
      <v-list-item v-for="(step, index) of recording.steps" v-bind:key="index" class="px-4">
        <v-list-item-content class="py-1">
          <v-list-item-title>{{ index + 1 }}. {{ stepFilename(step, recording.baseline) }}</v-list-item-title>
          <v-list-item-subtitle>{{ step.label }}</v-list-item-subtitle>
          <v-list-item-subtitle v-if="step.warnings.length" class="orange--text">
            {{ step.warnings[0] }}
          </v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action class="my-0">
          <v-btn icon x-small @click="removeRecordedStep(index)" title="remove this page">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>
    </v-list>
    <v-alert v-if="addError" dense type="error" class="mx-4 my-2">{{ addError }}</v-alert>
    <v-card-actions>
      <v-btn
        color="primary"
        :disabled="onMacrosPage"
        @click="askFilename"
        data-cy="macro-add-page"
        :title="onMacrosPage ? 'open the page you want to print first' : 'add this page to the macro'"
      >
        <v-icon left>mdi-file-pdf-box</v-icon>
        Add this page
      </v-btn>
      <v-spacer/>
      <v-btn text color="success" :disabled="recording.steps.length === 0" @click="finishRecording"
             data-cy="macro-finish">
        Finish
      </v-btn>
      <v-btn text @click="confirmCancelRecording">Cancel</v-btn>
    </v-card-actions>

    <!-- name the pdf before adding the page -->
    <v-dialog v-model="showFilenameDialog" max-width="520" @keydown.enter="confirmFilename">
      <v-card>
        <v-card-title class="subtitle-1">Name this page's pdf</v-card-title>
        <v-card-text>
          <v-text-field
            ref="filenameInput"
            v-model="filename"
            label="File name"
            hint="the first production day and the page's production or shipping group are added in front"
            persistent-hint
            autofocus
            data-cy="macro-filename"
          />
          <div class="mt-3">
            Will be saved as
            <code data-cy="macro-filename-preview">{{ filenamePreview }}</code>
            <span v-if="!recording.baseline && !inferredBaseline" class="grey--text">
              (the date prefix is added once an added page names its dates)
            </span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="showFilenameDialog = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!filename.trim()" @click="confirmFilename" data-cy="macro-filename-ok">
            Add page
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
/**
 * Floating panel for recording a print macro. Mounted once in App.vue so it outlives
 * page changes: the admin opens each page they print and adds it here (the store holds
 * the recording; this component has the router). Nothing is printed while recording -
 * the pages are rendered on the server when the macro runs.
 */
import {mapActions, mapState} from 'vuex';
import {describeTarget, inferBaseline, routeNameToWords, slugify, stepFilename} from '@/utils/macro-steps';

export default {
  name: 'MacroRecorderBar',
  data() {
    return {
      showSteps: true,
      addError: null,
      showFilenameDialog: false,
      filename: '',
    };
  },
  computed: {
    ...mapState('macros', ['recording']),
    onMacrosPage() {
      return this.$route.name === 'PrintMacros';
    },
    /** the production days the current page shows, when the recording has none yet */
    inferredBaseline() {
      return inferBaseline(this.$route);
    },
    filenamePreview() {
      const baseline = (this.recording && this.recording.baseline) || this.inferredBaseline;
      const {path, query} = this.$route;
      return stepFilename({filename: this.filename, label: this.pageLabel, path, query}, baseline);
    },
    pageLabel() {
      return routeNameToWords(this.$route.name) || this.$route.path;
    },
  },
  methods: {
    ...mapActions('macros', ['recordStep', 'removeRecordedStep', 'stopRecording', 'cancelRecording']),
    describeTarget,
    stepFilename,
    notify(message, color = 'info') {
      this.$store.commit('showSnackbar', {message, color, timeout: 5000});
    },
    askFilename() {
      this.addError = null;
      this.filename = slugify(this.pageLabel);
      this.showFilenameDialog = true;
    },
    confirmFilename() {
      if (!this.filename.trim()) {
        return;
      }
      this.showFilenameDialog = false;
      this.addCurrentPage(this.filename.trim());
    },
    async addCurrentPage(filename) {
      try {
        const {name, path, query} = this.$route;
        const step = await this.recordStep({route: {name, path, query}, filename});
        this.notify(`added ${stepFilename(step, this.recording.baseline)}`, 'success');
      } catch (e) {
        console.error('adding the page failed', e);
        this.addError = `could not add the page: ${e.message || e}`;
      }
    },
    async finishRecording() {
      const name = prompt('Name this macro', this.recording.name || '');
      if (name === null) {
        return;
      }
      let macro;
      try {
        macro = await this.stopRecording({name});
      } catch (e) {
        console.error('saving the macro failed', e);
        this.notify(`could not save the macro: ${e.message || e}`, 'error');
        return;
      }
      if (macro) {
        this.notify(`saved macro "${macro.name}" with ${macro.steps.length} pages`, 'success');
        // land on the macros page with the new macro selected, ready to play
        this.$router.push({name: 'PrintMacros', query: {play: slugify(macro.name)}}).catch(() => {});
      }
    },
    confirmCancelRecording() {
      if (this.recording.steps.length === 0 || confirm('Discard this recording?')) {
        this.cancelRecording();
      }
    },
  },
};
</script>

<style scoped>
.macro-recorder-bar {
  position: fixed;
  left: 16px;
  bottom: 16px;
  z-index: 200;
  max-height: 70vh;
  overflow-y: auto;
}

.macro-recording-dot {
  animation: macro-blink 1.2s ease-in-out infinite;
}

@keyframes macro-blink {
  50% {
    opacity: 0.3;
  }
}
</style>
